import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { cx } from './cx.js';
import { Badge } from './data.js';
/**
 * A connector's health AND its circuit state (§2.1). An open circuit is not the
 * same as an unhealthy channel — the breaker may have tripped while the channel
 * recovered — so both are shown rather than collapsed into one light.
 */
export function ConnectorStatusBadge({ healthy, circuitState, detail }) {
    const circuitTone = circuitState === 'open' ? 'error' : circuitState === 'half_open' ? 'warning' : 'neutral';
    return (_jsxs("span", { className: "inline-flex items-center gap-1.5", children: [_jsx(Badge, { tone: healthy ? 'success' : 'error', children: healthy ? 'Healthy' : 'Unhealthy' }), circuitState !== undefined && circuitState !== 'closed' && (_jsxs(Badge, { tone: circuitTone, children: ["Circuit ", circuitState.replace('_', '-')] })), detail !== undefined && detail !== null && detail.length > 0 && (_jsx("span", { className: "text-xs text-ink-muted", children: detail }))] }));
}
// --- TextPostPreview ---
/** Asset types that ARE their text — a blank thumbnail hides their whole point. */
const TEXT_ASSET_TYPES = new Set(['article', 'campaign_brief', 'product']);
/**
 * A text asset rendered as a legible content card rather than an empty box (§20.3).
 * An article's value is its words, so the tile shows the headline and an excerpt —
 * set on brand navy with a gold overline — so the catalogue, the publish feed and
 * the approval gate all let you appreciate what a post says at a glance.
 */
/** A bare ULID (26-char Crockford base32) — an id standing in for a missing title. */
const ULID_RE = /^[0-9A-HJKMNP-TV-Z]{26}$/;
export function TextPostPreview({ title, body, kicker, className }) {
    // A title that is really just the asset id is not a headline — showing it as
    // one is worse than showing none, so fall back to the body alone.
    const heading = title !== undefined && title !== null && title.length > 0 && !ULID_RE.test(title)
        ? title
        : null;
    const excerpt = body !== undefined && body !== null && body.length > 0 ? body : null;
    if (heading === null && excerpt === null) {
        return _jsx("span", { className: "text-xs text-ink-muted", children: "No preview" });
    }
    return (_jsxs("div", { className: cx('flex h-full w-full flex-col gap-2 overflow-hidden bg-gradient-to-br from-navy-700 to-navy-900 px-5 py-4 text-left', className), children: [kicker !== undefined && kicker !== null && kicker.length > 0 && (_jsx("span", { className: "text-[10px] font-semibold uppercase tracking-[0.16em] text-gold-400", children: kicker })), heading !== null && (_jsx("p", { className: "line-clamp-3 font-heading text-lg font-bold leading-snug text-ink-inverse", children: heading })), excerpt !== null && (_jsx("p", { className: "line-clamp-4 whitespace-pre-line text-xs leading-relaxed text-navy-100", children: excerpt }))] }));
}
const ASSET_STATE_TONE = {
    draft: 'neutral',
    active: 'success',
    delisted: 'warning',
    archived: 'neutral',
};
export function AssetPreview({ title, mediaUrl, body, type, state, footer }) {
    // A URL that 404s or 403s (a private blob without a signed link, a legacy
    // record) must not leave a broken-image glyph with raw alt text — it degrades
    // to the same honest "No preview" a missing image gets. Reset on URL change so
    // a fresh source gets its own chance to load.
    const [errored, setErrored] = useState(false);
    useEffect(() => {
        setErrored(false);
    }, [mediaUrl]);
    const hasImage = mediaUrl !== undefined && mediaUrl !== null && !errored;
    // A text asset with no image is not a broken tile — it's an article. Render its
    // words rather than the same "No preview" a missing creative earns.
    const hasText = (body !== undefined && body !== null && body.length > 0) || TEXT_ASSET_TYPES.has(type);
    return (_jsxs("article", { className: "overflow-hidden rounded-lg border border-edge bg-surface shadow-card", children: [_jsx("div", { className: "flex aspect-[4/3] items-center justify-center overflow-hidden bg-surface-sunken", children: hasImage ? (_jsx("img", { src: mediaUrl, alt: title, loading: "lazy", onError: () => setErrored(true), className: "h-full w-full object-contain" })) : hasText ? (_jsx(TextPostPreview, { title: title, body: body ?? null, kicker: type })) : (
                // No image and no words is a fact worth stating, not a blank box.
                _jsx("span", { className: "text-xs text-ink-muted", children: "No preview" })) }), _jsxs("div", { className: "px-4 py-3", children: [_jsx("p", { className: "truncate font-semibold text-navy-700", title: title, children: title }), _jsxs("div", { className: "mt-2 flex flex-wrap items-center gap-1.5", children: [_jsx(Badge, { children: type }), _jsx(Badge, { tone: ASSET_STATE_TONE[state] ?? 'neutral', children: state })] }), footer !== undefined && _jsx("div", { className: "mt-3", children: footer })] })] }));
}
/** What a channel will accept, at a glance (§6.4 format spec). */
export function ChannelFormatPreview({ channel, width, height, maxFileSizeBytes, accepted, }) {
    // Scale the swatch so tall and wide formats are visually comparable.
    const ratio = width / height;
    const boxHeight = 88;
    const boxWidth = Math.round(boxHeight * ratio);
    return (_jsxs("div", { className: "flex items-center gap-3 rounded-md border border-edge bg-surface px-4 py-3", children: [_jsx("div", { className: "shrink-0 rounded-sm border border-edge-strong bg-surface-sunken", style: { width: `${boxWidth}px`, height: `${boxHeight}px` }, "aria-hidden": "true" }), _jsxs("div", { className: "min-w-0", children: [_jsx("p", { className: "text-sm font-semibold text-navy-700", children: channel }), _jsxs("p", { className: "font-mono text-xs text-ink-muted", children: [width, "\u00D7", height] }), maxFileSizeBytes !== undefined && maxFileSizeBytes > 0 && (_jsxs("p", { className: "text-xs text-ink-muted", children: ["max ", (maxFileSizeBytes / 1_000_000).toFixed(1), " MB"] })), accepted !== undefined && accepted.length > 0 && (_jsx("p", { className: "truncate text-xs text-ink-muted", children: accepted.join(', ') }))] })] }));
}
const NODE_STATE_TONE = {
    pending: 'neutral',
    queued: 'info',
    running: 'info',
    completed: 'success',
    failed: 'error',
    rolled_back: 'warning',
    skipped: 'warning',
};
const ACTION_GLYPH = {
    ingest: '⭳',
    transform: '⇄',
    render: '◨',
    approve: '✓',
    publish: '↗',
    verify: '⌕',
    notify: '◈',
};
/**
 * Compute execution layers for display: a node sits one level below its
 * deepest dependency. This mirrors the engine's own layering (§8.1) so the
 * picture matches what will actually run — a viewer that laid nodes out
 * differently from the executor would be actively misleading.
 */
function toLayers(nodes) {
    const byId = new Map(nodes.map((n) => [n.nodeId, n]));
    const depth = new Map();
    const resolve = (nodeId, seen) => {
        const cached = depth.get(nodeId);
        if (cached !== undefined)
            return cached;
        // Guard against a cycle so a malformed DAG renders instead of hanging.
        if (seen.has(nodeId))
            return 0;
        seen.add(nodeId);
        const node = byId.get(nodeId);
        const deps = node?.dependsOn ?? [];
        const level = deps.length === 0 ? 0 : Math.max(...deps.map((d) => resolve(d, new Set(seen)) + 1));
        depth.set(nodeId, level);
        return level;
    };
    for (const node of nodes)
        resolve(node.nodeId, new Set());
    const layers = [];
    for (const node of nodes) {
        const level = depth.get(node.nodeId) ?? 0;
        const layer = layers[level] ?? [];
        layer.push(node);
        layers[level] = layer;
    }
    return layers.map((l) => (l ?? []).slice().sort((a, b) => (a.nodeId < b.nodeId ? -1 : 1)));
}
export function CampaignDAGViewer({ nodes, onSelect, selected }) {
    const layers = toLayers(nodes);
    return (_jsx("div", { className: "overflow-x-auto rounded-lg border border-edge bg-surface-sunken p-4", children: _jsx("div", { className: "flex min-w-max flex-col gap-3", children: layers.map((layer, index) => (_jsxs("div", { className: "flex items-stretch gap-3", children: [_jsx("div", { className: "flex w-16 shrink-0 items-center", children: _jsxs("span", { className: "font-mono text-[11px] uppercase tracking-wide text-ink-muted", children: ["step ", index + 1] }) }), _jsx("div", { className: "flex flex-wrap gap-3", children: layer.map((node) => {
                            const tone = node.state !== undefined ? NODE_STATE_TONE[node.state] : undefined;
                            const isSelected = selected === node.nodeId;
                            return (_jsxs("button", { type: "button", onClick: () => onSelect?.(node.nodeId), className: cx('min-w-[11rem] rounded-md border bg-surface px-3 py-2 text-left transition-colors duration-fast', isSelected
                                    ? 'border-gold-500 ring-2 ring-gold-400'
                                    : 'border-edge-strong hover:border-navy-400'), children: [_jsxs("span", { className: "flex items-center gap-2", children: [_jsx("span", { "aria-hidden": "true", className: "text-navy-500", children: ACTION_GLYPH[node.action] ?? '•' }), _jsx("span", { className: "truncate text-sm font-semibold text-navy-700", children: node.nodeId })] }), _jsxs("span", { className: "mt-1 flex items-center gap-1.5", children: [_jsx(Badge, { children: node.action }), node.state !== undefined && (_jsx(Badge, { tone: tone ?? 'neutral', children: node.state.replace('_', ' ') }))] }), (node.dependsOn ?? []).length > 0 && (_jsxs("span", { className: "mt-1 block truncate text-[11px] text-ink-muted", children: ["after ", (node.dependsOn ?? []).join(', ')] }))] }, node.nodeId));
                        }) })] }, `layer-${String(index)}`))) }) }));
}
//# sourceMappingURL=domain.js.map